export type RadarMetric = {
  label: string;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  value: number;
};

export type RadarChartProps = {
  data: RadarMetric[];
  size?: number;
  padding?: number;
};

export const RadarChart: React.FC<RadarChartProps> = ({ data, size = 400, padding = 50 }) => {
  const radius = size / 2 - padding;
  const center = size / 2;
  const angleSlice = (2 * Math.PI) / data.length;

  const getPoint = (value: number, index: number, scale = 1) => {
    const angle = index * angleSlice - Math.PI / 2;
    const r = (value / 100) * radius * scale;
    const x = center + r * Math.cos(angle);
    const y = center + r * Math.sin(angle);
    return `${x},${y}`;
  };

  const polygonPoints = data.map((d, i) => getPoint(d.value, i)).join(" ");
  const iconOffset = 100;

  const positions = data.map((_, i) => getPoint(iconOffset, i).split(",").map(Number));

  return (
    <svg width={size} height={size} className="text-gray-800 mx-auto">
      {/* Web lines */}
      {[0.33, 0.66, 1].map((scale, idx) => (
        <polygon
          key={idx}
          points={data.map((_, i) => getPoint(100, i, scale)).join(" ")}
          fill="none"
          stroke="#B3B9CC"
          strokeWidth="1"
        />
      ))}

      {/* Central axes */}
      <line
        x1={center}
        y1={center - radius}
        x2={center}
        y2={center + radius}
        stroke="#B3B9CC"
        strokeWidth="1"
      />
      <line
        x1={center - radius}
        y1={center}
        x2={center + radius}
        y2={center}
        stroke="#B3B9CC"
        strokeWidth="1"
      />

      {/* Radar shape */}
      <polygon points={polygonPoints} fill="#00A8E480" />

      {/* Dots */}
      {data.map((d, i) => {
        const [x, y] = getPoint(d.value, i).split(",").map(Number);
        const color =
          d.value === 0
            ? "#9FA6BC"
            : d.value === 33
            ? "#00D6A8"
            : d.value === 66
            ? "#F3AC60"
            : "#FE6447";
        return <circle key={i} cx={x} cy={y} r={8} fill={color} />;
      })}

      {/* Icons and labels */}
      {positions.map(([x, y], i) => {
        const Icon = data[i].icon;
        const label = data[i].label;

        const transforms = [
          {
            dx: -10,
            dy: -30,
            iconT: "translate(0, -15)",
            textA: "middle",
            textDx: "10px",
            textDy: "10px",
          },
          {
            dx: 0,
            dy: 0,
            iconT: "translate(20, -10)",
            textA: "start",
            textDx: "15px",
            textDy: "15px",
          },
          {
            dx: 0,
            dy: 0,
            iconT: "translate(-10, 10)",
            textA: "middle",
            textDx: "0px",
            textDy: "35px",
          },
          {
            dx: 0,
            dy: 0,
            iconT: "translate(-35, -10)",
            textA: "end",
            textDx: "0px",
            textDy: "15px",
          },
        ];

        const t = transforms[i];

        return (
          <g
            key={i}
            transform={`translate(${x + (t.dx || 0)}, ${y + (t.dy || 0)})`}
            className="text-foreground"
          >
            <g transform={t.iconT}>
              <Icon />
            </g>
            <text
              className="font-light"
              textAnchor={t.textA as "start" | "middle" | "end"}
              alignmentBaseline="hanging"
              dx={t.textDx}
              dy={t.textDy}
            >
              {label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};
